import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, CalendarRange } from "lucide-react";
import { api, formatError } from "../../lib/api";
import { AdminHeader, AdminModal, AField } from "../../components/AdminLayout";
import ColorPicker from "../../components/ColorPicker";

const DOW = [["Sun", 0], ["Mon", 1], ["Tue", 2], ["Wed", 3], ["Thu", 4], ["Fri", 5], ["Sat", 6]];
const CATS = [["classes", "Classes"], ["socials", "Socials"], ["events", "Events"]];
const BLANK = { name: "", category: "classes", start_date: "", end_date: "", time: "19:00", weekdays: [], level: "All levels", instructor: "", capacity: 12, credit_cost: 1, location: "", color: "#9D4EDD" };

const scheduleText = (t) => `${(t.weekdays || []).map((d) => DOW[d][0]).join(", ")} · ${t.time} · ${t.start_date} → ${t.end_date}`;

export default function AdminTerms() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/terms").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const toggleDay = (d) => setModal((m) => ({ ...m, weekdays: m.weekdays.includes(d) ? m.weekdays.filter((x) => x !== d) : [...m.weekdays, d] }));

  const save = async () => {
    setBusy(true);
    try {
      if (modal.id) {
        const fields = { name: modal.name, level: modal.level, instructor: modal.instructor, color: modal.color };
        if (modal.category === "classes") { fields.capacity = parseInt(modal.capacity, 10) || 1; fields.credit_cost = parseInt(modal.credit_cost, 10) || 1; }
        else fields.location = modal.location;
        await api.put(`/admin/terms/${modal.id}`, fields);
        toast.success("Term updated — upcoming sessions changed");
      } else {
        if (!modal.name.trim()) { toast.error("Term name is required"); setBusy(false); return; }
        if (!modal.start_date || !modal.end_date) { toast.error("Pick start & end dates"); setBusy(false); return; }
        if (!modal.weekdays.length) { toast.error("Pick at least one day of the week"); setBusy(false); return; }
        const payload = { name: modal.name, category: modal.category, start_date: modal.start_date, end_date: modal.end_date, time: modal.time, weekdays: modal.weekdays, level: modal.level, instructor: modal.instructor, color: modal.color };
        if (modal.category === "classes") { payload.capacity = parseInt(modal.capacity, 10) || 12; payload.credit_cost = parseInt(modal.credit_cost, 10) || 1; }
        else payload.location = modal.location;
        const res = await api.post("/admin/terms", payload);
        toast.success(`Term created · ${res.data.sessions} sessions generated`);
      }
      setModal(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Upcoming sessions will be removed; past sessions & attendance are kept.`)) return;
    try { const r = await api.delete(`/admin/terms/${t.id}`); toast.success(`Deleted · ${r.data.removed_future} upcoming sessions removed`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const isClass = modal?.category === "classes";

  return (
    <div>
      <AdminHeader title="Terms" subtitle={`${items.length} recurring series`}
        action={<button onClick={() => setModal({ ...BLANK })} data-testid="admin-add-term" className="flex items-center gap-2 bg-[#D4AF37] text-black font-semibold rounded-full px-4 py-2.5 text-sm active:scale-95 transition-transform"><Plus size={16} /> New term</button>} />

      {items.length === 0 && <p className="text-[#A19DB0] text-sm">No terms yet. A term is a named season (e.g. “Term 1 · Jan–Mar”) that auto-generates a weekly recurring series.</p>}

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} data-testid={`admin-term-${t.id}`} className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: t.color || "#9D4EDD" }} /><h3 className="font-display text-xl font-semibold truncate">{t.name}</h3></div>
                <p className="text-[#A19DB0] text-xs mt-1 capitalize">{t.category}{t.instructor ? ` · ${t.instructor}` : ""}{t.level ? ` · ${t.level}` : ""}</p>
              </div>
              <span className="text-xs bg-[#9D4EDD]/20 text-[#c79bff] px-2.5 py-1 rounded-full shrink-0" data-testid={`term-sessions-${t.id}`}>{t.sessions} sessions</span>
            </div>
            <p className="text-[#A19DB0] text-sm mt-3">{scheduleText(t)}</p>
            <p className="text-[#A19DB0]/70 text-xs mt-1">{t.upcoming_sessions} upcoming</p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setModal({ ...BLANK, ...t })} data-testid={`edit-term-${t.id}`} className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 text-sm hover:bg-white/10"><Pencil size={14} /> Edit</button>
              <button onClick={() => del(t)} data-testid={`delete-term-${t.id}`} className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 text-sm text-[#E57373] hover:bg-white/10"><Trash2 size={14} /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <AdminModal open onOpenChange={() => setModal(null)} title={modal.id ? "Edit term" : "New term"} onSubmit={save} busy={busy}>
          <AField label="Term name" value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} data-testid="term-name" placeholder="Term 1 · Beginners Bachata" />
          <div>
            <label className="text-xs text-[#A19DB0]">Category</label>
            <div className="flex gap-2 mt-1">
              {CATS.map(([v, l]) => (
                <button key={v} type="button" disabled={!!modal.id} onClick={() => setModal({ ...modal, category: v })} data-testid={`term-cat-${v}`}
                  className={`flex-1 rounded-xl py-2 text-sm border transition-colors ${modal.category === v ? "bg-[#D4AF37] text-black border-[#D4AF37] font-semibold" : "bg-white/5 text-[#A19DB0] border-white/10"} ${modal.id ? "opacity-60" : ""}`}>{l}</button>
              ))}
            </div>
          </div>

          {!modal.id && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <AField label="Start date" type="date" value={modal.start_date} onChange={(e) => setModal({ ...modal, start_date: e.target.value })} data-testid="term-start" />
                <AField label="End date" type="date" value={modal.end_date} onChange={(e) => setModal({ ...modal, end_date: e.target.value })} data-testid="term-end" />
              </div>
              <div>
                <label className="text-xs text-[#A19DB0]">Repeat on</label>
                <div className="flex gap-1.5 mt-1">
                  {DOW.map(([lbl, d]) => (
                    <button key={d} type="button" onClick={() => toggleDay(d)} data-testid={`term-day-${d}`}
                      className={`flex-1 rounded-lg py-2 text-xs border transition-colors ${modal.weekdays.includes(d) ? "bg-[#D4AF37] text-black border-[#D4AF37] font-bold" : "bg-white/5 text-[#A19DB0] border-white/10"}`}>{lbl[0]}</button>
                  ))}
                </div>
              </div>
              <AField label="Time" type="time" value={modal.time} onChange={(e) => setModal({ ...modal, time: e.target.value })} data-testid="term-time" />
            </>
          )}

          {modal.id && <p className="text-[#A19DB0] text-xs bg-white/5 rounded-lg px-3 py-2">Editing updates all <b>upcoming</b> sessions. The schedule (dates/days) can’t be changed — delete & recreate to reschedule.</p>}

          <AField label="Instructor / host" value={modal.instructor || ""} onChange={(e) => setModal({ ...modal, instructor: e.target.value })} data-testid="term-instructor" />
          {isClass ? (
            <div className="grid grid-cols-3 gap-3">
              <AField label="Level" value={modal.level || ""} onChange={(e) => setModal({ ...modal, level: e.target.value })} data-testid="term-level" />
              <AField label="Capacity" type="number" value={modal.capacity} onChange={(e) => setModal({ ...modal, capacity: e.target.value })} data-testid="term-capacity" />
              <AField label="Passes" type="number" value={modal.credit_cost} onChange={(e) => setModal({ ...modal, credit_cost: e.target.value })} data-testid="term-credits" />
            </div>
          ) : (
            <AField label="Location" value={modal.location || ""} onChange={(e) => setModal({ ...modal, location: e.target.value })} data-testid="term-location" />
          )}
          <ColorPicker value={modal.color} onChange={(v) => setModal((m) => ({ ...m, color: v }))} />
        </AdminModal>
      )}
    </div>
  );
}
